import { useEffect, useState } from 'react';
import { adminApi } from '../../adminApi';
import { IconCheck, IconX, IconRefresh } from '../../components/Icon';
import { AdminHeader, Reveal } from './ui';

/**
 * Refund ledger. Rows are created by the user-facing cancel/refund flow
 * (UserController::cancelPlan). Auto-eligible refunds land as `approved`;
 * everything else as `pending` for a manual approve/reject here. Every action
 * also pings the admin Telegram.
 */
export default function AdminRefunds() {
  const [rows, setRows] = useState<any[]>([]);
  const [pending, setPending] = useState(0);
  const [filter, setFilter] = useState('all');
  const load = () => adminApi.refunds({ status: filter }).then(r => {
    if (r.status === 200) { setRows(r.data?.refunds || []); setPending(r.data?.pending_count || 0); }
  });
  useEffect(() => { load(); }, [filter]);

  const act = async (id: number, action: 'approve' | 'reject') => {
    if (!confirm(`${action === 'approve' ? 'Approve' : 'Reject'} this refund?`)) return;
    await adminApi.processRefund(id, action);
    load();
  };

  const badge = (s: string) => s === 'pending'
    ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
    : s === 'approved'
      ? 'bg-[color:var(--color-ok)]/15 text-[color:var(--color-ok)] border-[color:var(--color-ok)]/30'
      : 'bg-red-500/15 text-red-300 border-red-500/30';

  return (
    <div>
      <AdminHeader icon={IconRefresh} title="Refunds" subtitle="User cancel/refund requests. Auto-approved within 7 days & <5% usage; others need review." />
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="glass p-6 text-center"><div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Pending review</div><div className="h-title text-3xl font-extrabold">{pending}</div></div>
        <div className="glass p-6 text-center"><div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Showing</div><div className="h-title text-3xl font-extrabold">{rows.length}</div></div>
        <div className="glass p-6 text-center"><div className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)]">Filter</div>
          <select value={filter} onChange={e => setFilter(e.target.value)} className="mt-1 w-full rounded-xl bg-black/30 border border-[color:var(--color-line)] px-3 py-2 text-sm">
            <option value="all">All</option><option value="pending">Pending</option><option value="approved">Approved</option><option value="rejected">Rejected</option>
          </select>
        </div>
      </div>
      <Reveal className="mt-6 glass overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-[color:var(--color-ink-faint)] border-b border-[color:var(--color-line)]">
              <tr><th className="text-left px-6 py-4">User</th><th className="px-6 text-left">Plan</th><th className="px-6 text-right">Amount</th><th className="px-6 text-left">Order</th><th className="px-6 text-left">Reason</th><th className="px-6">Status</th><th className="px-6 text-left">When</th><th className="text-right px-6">Action</th></tr>
            </thead>
            <tbody>
              {rows.map((r: any) => (
                <tr key={r.id} className="border-b border-white/5 align-top">
                  <td className="px-6 py-4"><div className="font-semibold">{r.username}</div><div className="text-xs text-[color:var(--color-ink-faint)]">{r.email || '—'} · #{r.user_id}</div></td>
                  <td className="px-6"><span className="chip">{r.plan || '—'}</span></td>
                  <td className="px-6 text-right font-semibold">${Number(r.amount || 0).toFixed(2)}</td>
                  <td className="px-6 font-mono text-xs">{r.order_id || '—'}</td>
                  <td className="px-6 text-xs text-[color:var(--color-ink-dim)] max-w-[240px]">{r.reason || '—'}{r.auto_approved ? <span className="ml-1 chip chip-ok !py-0 !text-[10px]">auto</span> : null}</td>
                  <td className="px-6 text-center"><span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-bold ${badge(r.status)}`}>{r.status}</span></td>
                  <td className="px-6 text-xs text-[color:var(--color-ink-faint)]">{String(r.created_at || '').slice(0, 16)}</td>
                  <td className="px-6 text-right">
                    {r.status === 'pending' ? (
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => act(r.id, 'approve')} className="text-xs px-2 py-1 rounded-lg bg-[color:var(--color-ok)]/15 text-[color:var(--color-ok)] border border-[color:var(--color-ok)]/30 inline-flex items-center gap-1"><IconCheck width={12} height={12} /> Approve</button>
                        <button onClick={() => act(r.id, 'reject')} className="text-xs px-2 py-1 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 inline-flex items-center gap-1"><IconX width={12} height={12} /> Reject</button>
                      </div>
                    ) : <span className="text-xs text-[color:var(--color-ink-faint)]">{String(r.processed_at || '').slice(0, 16) || 'done'}</span>}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="px-6 py-12 text-center text-sm text-[color:var(--color-ink-faint)]">No refunds</td></tr>}
            </tbody>
          </table>
        </div>
      </Reveal>
    </div>
  );
}
